// Database → Access Logs: every database login decision through the AuthNull proxy.
//
// One row per decision from POST /databaseService/accessLog/list, newest first,
// paged on the server. Filters live in the URL (dbAccessLogModel.js), so a
// filtered view can be linked and survives a reload. Administrators only: the
// API answers 403 to anyone else, so the page does not even ask.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, DatePicker, Drawer, Input, Select, Table, Tooltip } from "antd";
import { RefreshCw, Search } from "lucide-react";
import dayjs from "dayjs";

import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import {
  anTableProps,
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  SquareBadge,
  StatusPill,
  TableCard,
  fmtDate,
  tableEmpty,
} from "../../../design-system/tableUi";
import { isAdminUser } from "../../../utils/helpers";
import { dbAccessLogErrorText, listDbAccessLog } from "../../../service/dbAccessLogService";
import { CopyButton, Note, PanelHead } from "../ssh-proxy/sshProxyUi";
import {
  CUSTOM_RANGE,
  DECISION_OPTIONS,
  DEFAULT_RANGE,
  PAGE_SIZE_OPTIONS,
  RANGES,
  REASON_LABELS,
  REASON_OPTIONS,
  TEXT_FILTERS,
  buildPayload,
  dbTypeLabel,
  filtersToParams,
  parseFilters,
  reasonLabel,
} from "./dbAccessLogModel";
import "./dbAccessLogs.css";

const { RangePicker } = DatePicker;

const TEXT_DEBOUNCE_MS = 450;
const CONTROL_HEIGHT = "2.25rem";

const ONE_LINE = { display: "block", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const fmtDay = (t) => (t ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "2-digit" }) : "—");
const fmtClock = (t) =>
  t ? new Date(t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }) : "";

const DECISION_PILL = {
  allowed: { label: "Allowed", tone: "success" },
  denied: { label: "Denied", tone: "danger" },
};

function DecisionPill({ decision }) {
  const d = DECISION_PILL[decision] || { label: decision || "—", tone: "neutral" };
  return <StatusPill status={decision} label={d.label} tone={d.tone} />;
}

function DbTypeBadge({ type }) {
  const label = dbTypeLabel(type);
  return label ? <SquareBadge>{label}</SquareBadge> : null;
}

/* ------------------------------------------------------------------ drawer */

function DetailRow({ label, value, hint, copy }) {
  const has = value !== undefined && value !== null && value !== "";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "7.5rem minmax(0, 1fr) auto",
        gap: "0.625rem",
        alignItems: "center",
        padding: "0.5rem 0",
        borderBottom: `1px solid ${AN_COLORS.line2}`,
      }}
    >
      <span style={{ fontSize: "0.75rem", color: AN_COLORS.muted }}>
        {label}
        {hint && <span style={{ display: "block", fontSize: "0.6875rem" }}>{hint}</span>}
      </span>
      <span
        style={{
          fontFamily: AN_MONO,
          fontSize: "0.75rem",
          color: has ? AN_COLORS.ink : AN_COLORS.muted,
          wordBreak: "break-all",
        }}
      >
        {has ? value : "—"}
      </span>
      {copy && has ? <CopyButton text={String(value)} /> : <span />}
    </div>
  );
}

function EventDetail({ event: e }) {
  return (
    <div style={{ display: "grid", gap: "1.25rem", fontFamily: AN_FONT }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", flexWrap: "wrap" }}>
        <DecisionPill decision={e.decision} />
        <span style={{ fontSize: "0.8125rem", color: AN_COLORS.ink2 }}>{reasonLabel(e.reasonCode, e.reason)}</span>
      </div>

      <div>
        <PanelHead title="Login" />
        <DetailRow label="Time" value={e.at ? `${fmtDate(e.at)} (${e.at})` : ""} />
        <DetailRow label="Decision" value={e.decision} />
        <DetailRow label="Reason code" value={e.reasonCode} />
        <DetailRow label="Reason" value={e.reason} />
      </div>

      <div>
        <PanelHead title="Person" />
        <DetailRow label="Email" value={e.email || "Unknown"} />
        <DetailRow label="User ID" value={e.userId} />
        <DetailRow label="Client IP" value={e.clientIp} copy />
      </div>

      <div>
        <PanelHead title="Database" />
        <DetailRow label="DB role" value={e.dbUser} />
        <DetailRow label="Database" value={e.database} />
        <DetailRow label="Type" value={dbTypeLabel(e.dbType)} />
        <DetailRow label="Host" value={e.dbHost} copy />
      </div>

      <div>
        <PanelHead title="Policy" />
        <DetailRow label="Name" value={e.policyName} />
        <DetailRow label="Action" value={e.policyAction} />
        <DetailRow label="Policy ID" value={e.policyId} copy />
      </div>

      <div>
        <PanelHead title="MFA" />
        <DetailRow label="Method" value={e.mfaMethod} />
        <DetailRow label="Status" value={e.mfaStatus} />
        <DetailRow label="Challenge" value={e.challengeId} copy />
      </div>

      <div>
        <PanelHead title="Identifiers" />
        <DetailRow label="Request ID" hint="For support" value={e.requestId} copy />
        <DetailRow label="Event ID" value={e.eventId} copy />
        <DetailRow label="Tenant" value={e.tenantId} />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- page */

function AccessLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  // Text inputs are drafts, written to the URL once typing settles.
  const [drafts, setDrafts] = useState(() => Object.fromEntries(TEXT_FILTERS.map((k) => [k, filters[k]])));
  const committed = useRef(Object.fromEntries(TEXT_FILTERS.map((k) => [k, filters[k]])));

  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  // A preset window ends "now"; Refresh moves it on.
  const [now, setNow] = useState(() => Date.now());

  const update = useCallback(
    (patch, { keepPage = false } = {}) => {
      const next = { ...filters, ...patch };
      if (!keepPage) next.page = 1;
      TEXT_FILTERS.forEach((k) => {
        committed.current[k] = String(next[k] || "").trim();
      });
      setSearchParams(filtersToParams(next));
    },
    [filters, setSearchParams]
  );

  // URL → drafts, when the URL changed for a reason other than our own typing
  // (a cleared chip, back/forward, a pasted link).
  useEffect(() => {
    const fromUrl = Object.fromEntries(TEXT_FILTERS.map((k) => [k, filters[k]]));
    const ours = TEXT_FILTERS.every((k) => committed.current[k] === fromUrl[k]);
    committed.current = fromUrl;
    if (!ours) setDrafts(fromUrl);
  }, [filters]);

  // drafts → URL, debounced.
  useEffect(() => {
    const changed = TEXT_FILTERS.filter((k) => String(drafts[k] || "").trim() !== committed.current[k]);
    if (!changed.length) return undefined;
    const timer = setTimeout(() => {
      update(Object.fromEntries(TEXT_FILTERS.map((k) => [k, String(drafts[k] || "").trim()])));
    }, TEXT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [drafts, update]);

  const payload = useMemo(() => buildPayload(filters, now), [filters, now]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    listDbAccessLog({
      payload,
      onSuccess: (data) => {
        if (!live) return;
        setLoading(false);
        setEvents(Array.isArray(data.events) ? data.events : []);
        setTotal(Number(data.total) || 0);
      },
      onFailure: (err) => {
        if (!live) return;
        setLoading(false);
        setEvents([]);
        setTotal(0);
        setError(dbAccessLogErrorText(err));
      },
    });
    return () => {
      live = false;
    };
  }, [payload]);

  const setDraft = (k) => (e) => {
    const v = e.target.value;
    setDrafts((d) => ({ ...d, [k]: v }));
  };
  const clearText = (k) => {
    setDrafts((d) => ({ ...d, [k]: "" }));
    update({ [k]: "" });
  };

  const rangeLabel =
    filters.range === CUSTOM_RANGE
      ? [filters.from && fmtDate(filters.from), filters.to && fmtDate(filters.to)].filter(Boolean).join(" to ")
      : RANGES.find((r) => r.value === filters.range)?.label;

  const applied = [
    filters.email && { key: "email", label: `Person: ${filters.email}`, clear: () => clearText("email") },
    filters.dbUser && { key: "dbUser", label: `DB role: ${filters.dbUser}`, clear: () => clearText("dbUser") },
    filters.database && { key: "database", label: `Database: ${filters.database}`, clear: () => clearText("database") },
    filters.clientIp && { key: "clientIp", label: `Client IP: ${filters.clientIp}`, clear: () => clearText("clientIp") },
    filters.decision && {
      key: "decision",
      label: `Decision: ${DECISION_OPTIONS.find((o) => o.value === filters.decision)?.label || filters.decision}`,
      clear: () => update({ decision: "" }),
    },
    filters.reasonCode && {
      key: "reasonCode",
      label: `Reason: ${reasonLabel(filters.reasonCode)}`,
      clear: () => update({ reasonCode: "" }),
    },
    filters.range !== DEFAULT_RANGE && {
      key: "range",
      label: `Time: ${rangeLabel || "Custom range"}`,
      clear: () => update({ range: DEFAULT_RANGE, from: "", to: "" }),
    },
  ].filter(Boolean);

  const clearAll = () => {
    setDrafts(Object.fromEntries(TEXT_FILTERS.map((k) => [k, ""])));
    update({ email: "", dbUser: "", database: "", clientIp: "", decision: "", reasonCode: "", range: DEFAULT_RANGE, from: "", to: "" });
  };

  // An unknown code in the URL still appears in the dropdown, as itself.
  const reasonOptions =
    filters.reasonCode && !REASON_LABELS[filters.reasonCode]
      ? [...REASON_OPTIONS, { value: filters.reasonCode, label: filters.reasonCode }]
      : REASON_OPTIONS;

  const columns = [
    {
      title: "Time",
      key: "at",
      width: "5.75rem",
      render: (_, r) => (
        <Tooltip title={fmtDate(r.at)}>
          <span style={{ display: "grid", fontFamily: AN_MONO, fontSize: "0.75rem", lineHeight: 1.45 }}>
            <span style={{ color: AN_COLORS.ink }}>{fmtClock(r.at)}</span>
            <span style={{ color: AN_COLORS.muted }}>{fmtDay(r.at)}</span>
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Person",
      key: "email",
      render: (_, r) =>
        r.email ? (
          <Tooltip title={r.email}>
            <span style={{ ...ONE_LINE, fontWeight: 500, color: AN_COLORS.ink }}>{r.email}</span>
          </Tooltip>
        ) : (
          <span style={{ color: AN_COLORS.muted }}>Unknown</span>
        ),
    },
    { title: "DB role", key: "dbUser", width: "7.5rem", render: (_, r) => <MonoCell>{r.dbUser}</MonoCell> },
    {
      title: "Database",
      key: "database",
      width: "8.5rem",
      render: (_, r) => (
        <span style={{ display: "grid", gap: "0.25rem", minWidth: 0, justifyItems: "start" }}>
          <MonoCell title={[r.database, r.dbHost].filter(Boolean).join(" on ") || undefined}>{r.database}</MonoCell>
          <DbTypeBadge type={r.dbType} />
        </span>
      ),
    },
    { title: "Client IP", key: "clientIp", width: "8.25rem", render: (_, r) => <MonoCell>{r.clientIp}</MonoCell> },
    { title: "Decision", key: "decision", width: "6.5rem", render: (_, r) => <DecisionPill decision={r.decision} /> },
    {
      title: "Reason",
      key: "reason",
      render: (_, r) => (
        <Tooltip title={r.reason || undefined}>
          <span style={{ ...ONE_LINE, color: AN_COLORS.ink2 }}>{reasonLabel(r.reasonCode, r.reason)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Policy",
      key: "policy",
      width: "7.5rem",
      render: (_, r) =>
        r.policyName ? (
          <Tooltip title={r.policyAction ? `${r.policyName} (${r.policyAction})` : r.policyName}>
            <span style={{ ...ONE_LINE, color: AN_COLORS.ink2 }}>{r.policyName}</span>
          </Tooltip>
        ) : (
          <span style={{ color: AN_COLORS.muted }}>—</span>
        ),
    },
  ];

  const emptyText = applied.length
    ? "No database logins match these filters."
    : "No database logins recorded yet. Logins through the AuthNull proxy appear here.";

  const customValue =
    filters.range === CUSTOM_RANGE ? [filters.from ? dayjs(filters.from) : null, filters.to ? dayjs(filters.to) : null] : null;

  const textField = (k, label, placeholder, withIcon) => (
    <FilterField label={label}>
      <Input
        allowClear
        value={drafts[k]}
        onChange={setDraft(k)}
        prefix={withIcon ? <Search size={14} color={AN_COLORS.muted} /> : undefined}
        placeholder={placeholder}
        style={{ height: CONTROL_HEIGHT }}
      />
    </FilterField>
  );

  return (
    <PageShell>
      <PageHeader
        eyebrow="Database · Logs"
        title="Access Logs"
        subtitle="Every database login decision made by the AuthNull proxy: who, as which role, to which database, and why."
        chips={<CountChip count={total} label={total === 1 ? "login" : "logins"} />}
        actions={
          <Button icon={<RefreshCw size={14} style={{ verticalAlign: "-0.125rem" }} />} onClick={() => setNow(Date.now())} loading={loading}>
            Refresh
          </Button>
        }
      />

      {error && (
        <div style={{ marginBottom: "0.875rem" }}>
          <Note tone="danger">{error}</Note>
        </div>
      )}

      <FilterCard
        columns="repeat(4, minmax(0, 1fr))"
        applied={applied}
        onClearAll={clearAll}
        showing={events.length}
        total={total}
      >
        {textField("email", "Person", "Any part of an email", true)}
        {textField("dbUser", "DB role", "Exact role name")}
        {textField("database", "Database", "Exact database name")}
        {textField("clientIp", "Client IP", "e.g. 203.0.113.14")}
        <FilterField label="Decision">
          <Select
            allowClear
            placeholder="Any decision"
            value={filters.decision || undefined}
            onChange={(v) => update({ decision: v || "" })}
            options={DECISION_OPTIONS}
            style={{ width: "100%", height: CONTROL_HEIGHT }}
          />
        </FilterField>
        <FilterField label="Reason">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Any reason"
            value={filters.reasonCode || undefined}
            onChange={(v) => update({ reasonCode: v || "" })}
            options={reasonOptions}
            style={{ width: "100%", height: CONTROL_HEIGHT }}
          />
        </FilterField>
        <FilterField label="Time range">
          <Select
            value={filters.range}
            onChange={(v) => {
              if (v === CUSTOM_RANGE) {
                const to = new Date();
                update({ range: CUSTOM_RANGE, from: new Date(to.getTime() - 24 * 3600 * 1000).toISOString(), to: to.toISOString() });
              } else {
                setNow(Date.now());
                update({ range: v, from: "", to: "" });
              }
            }}
            options={[...RANGES.map(({ value, label }) => ({ value, label })), { value: CUSTOM_RANGE, label: "Custom range" }]}
            style={{ width: "100%", height: CONTROL_HEIGHT }}
          />
        </FilterField>
        {filters.range === CUSTOM_RANGE ? (
          <FilterField label="From and to">
            <RangePicker
              showTime={{ format: "HH:mm" }}
              format="MMM D, HH:mm"
              allowEmpty={[true, true]}
              value={customValue}
              onChange={(vals) =>
                update({
                  range: CUSTOM_RANGE,
                  from: vals?.[0] ? vals[0].toDate().toISOString() : "",
                  to: vals?.[1] ? vals[1].toDate().toISOString() : "",
                })
              }
              style={{ width: "100%", height: CONTROL_HEIGHT }}
            />
          </FilterField>
        ) : (
          <div />
        )}
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          className={`${anTableProps.className} dbal-table`}
          columns={columns}
          dataSource={events}
          rowKey={(r) => r.eventId || `${r.at}-${r.requestId}`}
          loading={loading}
          tableLayout="fixed"
          onRow={(r) => ({ onClick: () => setSelected(r) })}
          locale={tableEmpty(loading, emptyText)}
          pagination={{
            current: filters.page,
            pageSize: filters.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
            hideOnSinglePage: total === 0,
            showTotal: (n, [a, b]) => `${a}–${b} of ${n.toLocaleString()}`,
            onChange: (page, pageSize) =>
              pageSize !== filters.pageSize ? update({ pageSize, page: 1 }) : update({ page }, { keepPage: true }),
            style: { padding: "0 1rem" },
          }}
        />
      </TableCard>

      <div style={{ marginTop: "0.625rem", fontSize: "0.75rem", color: AN_COLORS.muted }}>
        Logins from the web database terminal in the console show the console server's address as the client IP.
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        width="32.5rem"
        title={selected ? `${selected.email || "Unknown"} as ${selected.dbUser || "unknown role"}` : ""}
      >
        {selected && <EventDetail event={selected} />}
      </Drawer>
    </PageShell>
  );
}

export default function DbAccessLogsPage() {
  if (!isAdminUser()) {
    return (
      <PageShell>
        <PageHeader eyebrow="Database · Logs" title="Access Logs" />
        <div style={{ fontSize: "0.875rem", color: AN_COLORS.ink2 }}>Only administrators can see access logs.</div>
      </PageShell>
    );
  }
  return <AccessLogs />;
}
